/**
 * Логотип организации. Файл никуда не загружается: картинка кропается в квадрат
 * прямо в браузере и уезжает в настройки data:-URL'ом. Так у организации не
 * появляется требования держать хостинг ради одной картинки, а сервер не
 * начинает раздавать файлы — чего он делать не умеет и не должен.
 */

/** Сторона готового квадрата. 512 хватает и шапке, и превью на ретине. */
const SIDE = 512;

/** Что принимаем от администратора до кропа. */
export const ACCEPT = 'image/jpeg,image/png,image/webp';
const TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 2 * 1024 * 1024;
const MIN_SIDE = 256;

export type LogoError =
  | { kind: 'type' }
  | { kind: 'size' }
  | { kind: 'small'; width: number; height: number }
  | { kind: 'broken' };

export const logoErrorText = (e: LogoError): string => {
  switch (e.kind) {
    case 'type': return 'Подойдёт jpg, png или webp';
    case 'size': return 'Файл больше 2 МБ — возьмите картинку полегче';
    case 'small': return `Картинка ${e.width}×${e.height} — нужна хотя бы 256×256`;
    default: return 'Не удалось прочитать картинку';
  }
};

export class LogoRejected extends Error {
  constructor(readonly reason: LogoError) {
    super(logoErrorText(reason));
    this.name = 'LogoRejected';
  }
}

/**
 * Файл → квадратный data:-URL. Кроп по центру: логотип почти всегда и так
 * квадратный, а спрашивать рамку ради редкого случая — лишний шаг в форме,
 * которую и без того заполняют один раз.
 *
 * Проверки идут до кропа, чтобы ошибка называла исходный файл, а не результат.
 */
export async function cropToSquare(file: File): Promise<string> {
  if (!TYPES.includes(file.type)) throw new LogoRejected({ kind: 'type' });
  if (file.size > MAX_BYTES) throw new LogoRejected({ kind: 'size' });

  const bitmap = await load(file);
  try {
    if (bitmap.width < MIN_SIDE || bitmap.height < MIN_SIDE) {
      throw new LogoRejected({ kind: 'small', width: bitmap.width, height: bitmap.height });
    }

    const side = Math.min(bitmap.width, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = SIDE;
    canvas.height = SIDE;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new LogoRejected({ kind: 'broken' });
    ctx.drawImage(
      bitmap,
      (bitmap.width - side) / 2, (bitmap.height - side) / 2, side, side,
      0, 0, SIDE, SIDE,
    );
    // png сохраняет прозрачность — у логотипов она бывает; остальное жмём в jpeg.
    return file.type === 'image/png'
      ? canvas.toDataURL('image/png')
      : canvas.toDataURL('image/jpeg', 0.88);
  } finally {
    if ('close' in bitmap) bitmap.close();
  }
}

/** ImageBitmap там, где он есть; в остальных клиентах — обычный <img>. */
async function load(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      /* формат не по зубам — пробуем через <img> */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new LogoRejected({ kind: 'broken' }));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
